import React, { useState, useEffect } from 'react';
import { Typography, Container, InputLabel, MenuItem, FormControl, Select, Stack, Box, Button } from '@mui/material';
import Grid from '@mui/material/Grid';
import LoadingOverlay from '../../loadingOverlay/LoadingOverlay';
import AlertBar from '../../alertBar/AlertBar';
import useSnackbar from '../../hooks/useSnackbar';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import enLocale from 'date-fns/locale/en-GB';
import { addDays, addYears, isEqual, format } from 'date-fns';
import { createWeeklyPeriods, createMonthlyPeriods, createQuarterlyPeriods } from '../reportingPeriod/ReportingPeriods';
import Period from './Period';
import { usePutMutation } from '../../hooks/useAuthenticatedMutation';
import useSuspenseFetch from '../../hooks/useSuspenseFetch';
import { getReportingPeriod } from './ReportingPeriods';

const ReportingPeriod = ({ onClose }) => {
    const { showSnackbar } = useSnackbar();
    const { data: account } = useSuspenseFetch('account');
    const putMutation = usePutMutation('account');

    const reportingPeriod = getReportingPeriod(account);
    const today = new Date();
    const thisYear = today.getFullYear();
    const defaultOrigin = reportingPeriod?.startDate ? new Date(reportingPeriod.startDate) : new Date(thisYear, 0, 1);
    const defaultType = reportingPeriod?.type ?? 'monthly';

    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [show, setShow] = useState(true);
    const [isGenerating, setIsGenerating] = useState(false);
    const [type, setType] = useState(defaultType);
    const [origin, setOrigin] = useState(defaultOrigin);

    const [examplePeriods, setExamplePeriods] = useState(null);

    const calendarCount = type === 'monthly' ? 1 : type === 'quarterly' ? 3 : 2;

    const typeLabel = 'Period Type';
    const dateLabel = 'Start Date';

    const handleCreateExamplePeriods = (type, origin) => {
        const start = new Date(origin);

        const end = addDays(addYears(new Date(origin), 1), -1);
        const fourWeeklyEnd = addYears(new Date(origin), 1);

        let periods = [];

        switch (type) {
            case '4weekly':
                periods = createWeeklyPeriods(start, fourWeeklyEnd, true);

                break;

            case 'quarterly':
                periods = createQuarterlyPeriods(start, end);

                break;

            //case 'monthly':
            default:
                periods = createMonthlyPeriods(start, end);
        }

        setExamplePeriods(periods);
    };

    const onSubmit = async e => {
        e.preventDefault();

        const reportingPeriod = {
            reportingPeriodType: type,
            reportingPeriodStartDate: format(origin, 'yyyy-MM-dd')
        };

        const onSuccess = () => {
            setHasUnsavedChanges(false);
            showSnackbar('Changes saved', 'success');
            onClose();
        };

        putMutation.mutate(reportingPeriod, { onSuccess });
    };

    const handleChangeType = event => {
        setShow(false);
        setType(event.target.value);
        setOrigin(new Date(thisYear, 0, 1));
    };

    const handleChangeOrigin = origin => {
        setShow(false);
        setOrigin(origin);
    };

    const handleRevert = () => {
        setShow(false);
        setOrigin(defaultOrigin);
        setType(defaultType);
        handleCreateExamplePeriods(defaultType, defaultOrigin);
    };

    const handleGeneratePeriods = () => {
        setIsGenerating(true);
        setShow(true);
    };

    useEffect(() => {
        // This prevents handleCreateExamplePeriods blocking initial render
        const timer = setTimeout(() => {
            handleCreateExamplePeriods(type, origin);
        }, 0);

        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        const isDateChanged = !reportingPeriod?.startDate
            ? !isEqual(origin, new Date(thisYear, 0, 1))
            : !isEqual(origin, new Date(reportingPeriod.startDate));

        (reportingPeriod === null && type === 'monthly')
            ? setHasUnsavedChanges(false)
            : setHasUnsavedChanges(type !== reportingPeriod?.type || isDateChanged);
    }, [type, origin]);

    useEffect(() => {
        if (examplePeriods) {
            isGenerating && setIsGenerating(false);
        }
    }, [examplePeriods]);

    useEffect(() => {
        isGenerating && handleCreateExamplePeriods(type, origin);
    }, [isGenerating]);

    return (
        <>
            <AlertBar
                shown={hasUnsavedChanges}
                action={(
                    <Stack direction="row" spacing={1}>
                        <Button
                            size="small"
                            loading={putMutation.isPending}
                            onClick={onSubmit}
                            loadingPosition="start"
                        >
                            Save Changes
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            loading={putMutation.isPending}
                            onClick={handleRevert}
                            loadingPosition="start"
                        >
                            Revert
                        </Button>
                    </Stack>
                )}
                positionTop={0}
            >
                You have unsaved changes
            </AlertBar>
            <Container sx={{ padding: 3 }} maxWidth="false">
                <Stack spacing={3}>
                    <Box>
                        <Stack spacing={2}>
                            <Typography variant="body1">
                                A Reporting Period, also known as an 'accounting period', is a specific period of time when a company's financial performance and position are reported and analysed. We'll group your Reports data according to the Reporting Periods you set below, allowing you to easily compare between periods.
                            </Typography>
                            <Typography variant="body1">
                                Update {typeLabel} (and {dateLabel} where applicable) to change how your reports data is calculated. Periods are automatically generated from your account inception to infinity; those generated below are just a sample so you can check the dates work for you.
                            </Typography>
                        </Stack>
                    </Box>
                    <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }} sx={{ alignItems: 'flex-start' }}>
                        <FormControl size="small" margin="none">
                            <InputLabel id="period-label">{typeLabel}</InputLabel>
                            <Select
                                labelId="period-label"
                                value={type}
                                label={typeLabel}
                                onChange={handleChangeType}
                                size="small"
                                margin="none"
                            >
                                <MenuItem value="4weekly">4 Weekly (13 periods)</MenuItem>
                                <MenuItem value="monthly">Monthly (12 periods)</MenuItem>
                                <MenuItem value="quarterly">Quarterly (4 periods)</MenuItem>
                            </Select>
                        </FormControl>
                        {type === '4weekly' && (
                            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enLocale}>
                                <DatePicker
                                    mask="__/__/____"
                                    label={dateLabel}
                                    value={origin}
                                    onChange={handleChangeOrigin}
                                    slotProps={{
                                        toolbar: {
                                            hidden: false
                                        },
                                        textField: {
                                            size: 'small'
                                        }
                                    }}
                                />
                            </LocalizationProvider>
                        )}
                        <Box>
                            <Button
                                loading={isGenerating}
                                onClick={handleGeneratePeriods}
                                loadingPosition="start"
                            >
                                Generate Periods
                            </Button>
                        </Box>
                    </Stack>
                    {(examplePeriods && show && !isGenerating) && (
                        <Grid container spacing={2}>
                            <Grid size={12}>
                                <Typography variant="h6">
                                    Your Reporting Periods for this year
                                </Typography>
                            </Grid>
                            {examplePeriods.map((e, i, a) => (
                                <Grid
                                    key={i}
                                    size={{
                                        xs: 'auto',
                                        sm: calendarCount > 1 ? 'auto' : 6,
                                        lg: calendarCount > 1 ? 'auto' : 4,
                                        xl: calendarCount > 1 ? 'auto' : 3
                                    }}
                                >
                                    <Period
                                        period={i + 1}
                                        dates={e}
                                        totalPeriods={a.length}
                                        calendarCount={calendarCount}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </Stack>
            </Container>
            {isGenerating && <LoadingOverlay />}
        </>
    );
};

export default ReportingPeriod;